import { useRef, useState } from "react";
import styled from "styled-components";
import { GoTrash, GoPlus } from "react-icons/go";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import { formatCPF } from "../../Funcoes/FormatCPF";
import { formatarData } from "../../Funcoes/FormatarData";
import ModalCelulaInfo from "./ModalCelulaInfo";
import ModalCelulaMunicipio from "./ModalCelulaMunicipio";
import ModalCelulaHorario from "./ModalCelulaHorario";

const CAMPOS_INFO = [
    { campo: "nome", label: "NOME" },
    { campo: "dataNasc", label: "DATA DE NASC" },
    { campo: "cpf", label: "CPF" },
    { campo: "rg", label: "RG" },
    { campo: "centroCusto", label: "CENTRO DE CUSTO" }
];

const Titulo = styled.h3`
    margin: 0 0 14px 0;
    font-size: 15px;
    color: #263B4B;
    text-align: center;
`;
const TabelaWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;
const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    & thead th {
        background-color: #00A651;
        color: #000;
        font-weight: 700;
        padding: 8px 10px;
        border: 1px solid #ffffff;
        text-align: center;
        white-space: nowrap;
    }
    & tbody td {
        border: 1px solid #e2e8f0;
        padding: 0;
        text-align: center;
        white-space: nowrap;
    }
`;
const CelulaClicavel = styled.td`
    cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
    color: ${({ vazia }) => (vazia ? "#94a3b8" : "#1f2937")};
    padding: 8px 10px !important;
    &:hover {
        background-color: ${({ disabled }) => (disabled ? "transparent" : "#eff6ff")};
    }
`;
const CelulaBusca = styled.td`
    padding: 4px 6px !important;
    min-width: 150px;
`;
const BuscaWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
const SelectTipo = styled.select`
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 4px 6px;
    font-size: 11px;
    font-weight: 700;
    color: #334155;
    background-color: #f8fafc;
    outline: none;
`;
const InputBusca = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 12px;
    text-align: center;
    outline: none;
    background-color: #fff;
    &:focus {
        border-color: #2563eb;
        background-color: #eff6ff;
    }
`;
const CelulaAcao = styled.td`
    padding: 8px 10px !important;
`;
const Lixeira = styled(GoTrash)`
    height: 16px;
    width: 16px;
    color: #e11d48;
    cursor: pointer;
    &:hover {
        color: #be123c;
    }
`;
const BotaoAdicionarLinha = styled.button`
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px dashed #94a3b8;
    border-radius: 8px;
    background-color: transparent;
    color: #334155;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    &:hover {
        border-color: #2563eb;
        color: #2563eb;
    }
`;
const Rodape = styled.div`
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;
const BotaoSalvar = styled.button`
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background-color: #263B4B;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    margin-left: auto;
    &:hover {
        background-color: #1d2f3d;
    }
    &:disabled {
        opacity: .6;
        cursor: not-allowed;
    }
`;

const criarLinha = (id) => ({
    id,
    tipoBusca: "matricula",
    matricula: "",
    cpfBusca: "",
    buscando: false,
    colab: null,
    deMunicip: "",
    deEstado: "",
    paraMunicip: "",
    paraEstado: "",
    horario: ""
});

const formatarLocal = (municipio, estado) => {
    if (!municipio) return "";
    return estado ? `${municipio} / ${estado}` : municipio;
};

const formatarDataNasc = (data) => {
    if (!data) return "";
    try {
        return formatarData(String(data));
    } catch {
        return String(data);
    }
};

const exibirCampoColab = (campo, valor) => {
    if (!valor) return "—";
    if (campo === "cpf") return formatCPF(String(valor));
    if (campo === "dataNasc") return formatarDataNasc(valor);
    return valor;
};

const formatarHorario = (valor) => {
    if (!valor) return "";
    const [data, hora] = valor.split("T");
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano} ${hora}`;
};

const ModalNovaSolicitacao = ({ fetchSuccess }) => {

    const api = useApi();
    const { isModalOpen, openModal, closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const proximoId = useRef(2);
    const [linhas, setLinhas] = useState([criarLinha(1)]);
    const [salvando, setSalvando] = useState(false);

    // Célula selecionada para os modais de leitura/edição: { linhaId, campo }
    const [celula, setCelula] = useState(null);

    const atualizarLinha = (linhaId, patch) => {
        setLinhas((atual) => atual.map((linha) => (
            linha.id === linhaId ? { ...linha, ...patch } : linha
        )));
    };

    const adicionarLinha = () => {
        setLinhas((atual) => [...atual, criarLinha(proximoId.current)]);
        proximoId.current += 1;
    };

    const removerLinha = (linhaId) => {
        setLinhas((atual) => atual.filter((linha) => linha.id !== linhaId));
    };

    const buscarColabPorMatricula = async (linhaId, matricula) => {

        atualizarLinha(linhaId, { buscando: true, colab: null });

        try {

            const { data } = await api.post("/Painel/API/GetColabInfo/", { matricula });
            const colab = Array.isArray(data) ? data[0] : data;

            if (!colab?.NOME) {
                notifyError("Colaborador não encontrado.");
                atualizarLinha(linhaId, { buscando: false, colab: null });
                return;
            }

            atualizarLinha(linhaId, {
                buscando: false,
                colab: {
                    nome: colab.NOME,
                    dataNasc: colab.DATA_NASC || "",
                    cpf: colab.CPF ? String(colab.CPF).replace(/\D/g, "") : "",
                    rg: colab.RG || "",
                    centroCusto: colab.CENTRO_CUSTO || ""
                }
            });

        } catch (err) {
            console.error(err);
            notifyError("Erro ao buscar o colaborador.");
            atualizarLinha(linhaId, { buscando: false, colab: null });
        }

    };

    const buscarColabPorCpf = async (linhaId, cpf) => {

        atualizarLinha(linhaId, { buscando: true, colab: null });

        try {

            const { data } = await api.post("/Painel/API/SolicitacaoPassagem/GetByCpf/", { cpf });

            if (!data?.success || !data?.data?.nome) {
                notifyError(data?.message || "Admissão não encontrada para este CPF.");
                atualizarLinha(linhaId, { buscando: false, colab: null });
                return;
            }

            const adm = data.data;

            atualizarLinha(linhaId, {
                buscando: false,
                matricula: "",
                colab: {
                    nome: adm.nome || "",
                    dataNasc: adm.data_nasc || "",
                    cpf: adm.cpf ? String(adm.cpf).replace(/\D/g, "") : cpf,
                    rg: adm.rg || "",
                    centroCusto: adm.centro_custo || ""
                }
            });

        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao buscar admissão por CPF.";
            notifyError(msg);
            atualizarLinha(linhaId, { buscando: false, colab: null });
        }

    };

    const mudarTipoBusca = (linhaId, tipoBusca) => {
        atualizarLinha(linhaId, {
            tipoBusca,
            matricula: "",
            cpfBusca: "",
            colab: null,
            buscando: false
        });
    };

    const mudarMatricula = (linhaId, valor) => {

        const matricula = valor.replace(/\D/g, "");
        atualizarLinha(linhaId, { matricula, colab: null });

        if (matricula.length === 6) {
            buscarColabPorMatricula(linhaId, matricula);
        }

    };

    const mudarCpfBusca = (linhaId, valor) => {

        const cpf = valor.replace(/\D/g, "").slice(0, 11);
        atualizarLinha(linhaId, { cpfBusca: cpf, colab: null });

        if (cpf.length === 11) {
            buscarColabPorCpf(linhaId, cpf);
        }

    };

    const abrirCelula = (linhaId, campo, modalId) => {
        setCelula({ linhaId, campo });
        openModal(modalId);
    };

    const fecharCelula = () => {
        closeModal();
        setCelula(null);
    };

    const salvarCelula = (valor) => {
        if (celula.campo === "de" || celula.campo === "para") {
            const prefixo = celula.campo;
            atualizarLinha(celula.linhaId, {
                [`${prefixo}Municip`]: valor.municipio,
                [`${prefixo}Estado`]: valor.estado
            });
        } else {
            atualizarLinha(celula.linhaId, { [celula.campo]: valor });
        }
        fecharCelula();
    };

    const handleSalvar = async () => {

        const linhaIncompleta = linhas.find((linha) => {
            const buscaOk = linha.tipoBusca === "cpf"
                ? linha.cpfBusca?.length === 11 && !!linha.colab
                : linha.matricula?.length === 6 && !!linha.colab;

            return (
                !buscaOk ||
                !linha.deMunicip ||
                !linha.deEstado ||
                !linha.paraMunicip ||
                !linha.paraEstado ||
                !linha.horario
            );
        });

        if (linhaIncompleta) {
            notifyError("Preencha todos os campos de todas as linhas antes de salvar.");
            return;
        }

        if (salvando) return;

        const itens = linhas.map((linha) => ({
            matricula: linha.tipoBusca === "matricula" ? linha.matricula : "",
            nome: linha.colab?.nome || "",
            dataNasc: linha.colab?.dataNasc || "",
            cpf: linha.colab?.cpf || "",
            rg: linha.colab?.rg || "",
            centroCusto: linha.colab?.centroCusto || "",
            deMunicip: linha.deMunicip,
            deEstado: linha.deEstado,
            paraMunicip: linha.paraMunicip,
            paraEstado: linha.paraEstado,
            horario: linha.horario
        }));

        setSalvando(true);

        try {
            const { data } = await api.post("/Painel/API/SolicitacaoPassagem/Save/", { itens });

            if (!data?.success) {
                notifyError(data?.message || "Não foi possível salvar a solicitação.");
                return;
            }

            notifySuccess(data.message || "Solicitação salva com sucesso.");
            if (typeof fetchSuccess === "function") {
                fetchSuccess();
            }
            closeModal("novaSolicitacaoPassagem");
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao salvar a solicitação.";
            notifyError(msg);
        } finally {
            setSalvando(false);
        }
    };

    const linhaSelecionada = celula
        ? linhas.find((linha) => linha.id === celula.linhaId)
        : null;

    const labelCelulaInfo = celula
        ? CAMPOS_INFO.find((item) => item.campo === celula.campo)?.label
        : "";

    return (
        <>
            <Overlay state={true}>
                <Modal header={true} close={() => closeModal("novaSolicitacaoPassagem")} size="lg">
                    <Titulo>NOVA SOLICITAÇÃO DE PASSAGEM</Titulo>
                    <TabelaWrapper>
                        <Tabela>
                            <thead>
                                <tr>
                                    <th>BUSCA</th>
                                    {CAMPOS_INFO.map((item) => (
                                        <th key={item.campo}>{item.label}</th>
                                    ))}
                                    <th>DE</th>
                                    <th>PARA</th>
                                    <th>HORÁRIO</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((linha) => (
                                    <tr key={linha.id}>
                                        <CelulaBusca>
                                            <BuscaWrap>
                                                <SelectTipo
                                                    value={linha.tipoBusca}
                                                    onChange={(e) => mudarTipoBusca(linha.id, e.target.value)}
                                                >
                                                    <option value="matricula">MATRÍCULA</option>
                                                    <option value="cpf">CPF</option>
                                                </SelectTipo>
                                                {linha.tipoBusca === "matricula" ? (
                                                    <InputBusca
                                                        value={linha.matricula}
                                                        onChange={(e) => mudarMatricula(linha.id, e.target.value)}
                                                        maxLength={6}
                                                        placeholder="000000"
                                                    />
                                                ) : (
                                                    <InputBusca
                                                        value={linha.cpfBusca ? formatCPF(linha.cpfBusca) : ""}
                                                        onChange={(e) => mudarCpfBusca(linha.id, e.target.value)}
                                                        maxLength={14}
                                                        placeholder="000.000.000-00"
                                                    />
                                                )}
                                            </BuscaWrap>
                                        </CelulaBusca>
                                        {CAMPOS_INFO.map((item) => (
                                            <CelulaClicavel
                                                key={item.campo}
                                                disabled={!linha.colab}
                                                vazia={!linha.colab}
                                                onClick={() => {
                                                    if (!linha.colab) return;
                                                    abrirCelula(linha.id, item.campo, "celulaInfoPassagem");
                                                }}
                                            >
                                                {linha.buscando && item.campo === "nome"
                                                    ? "BUSCANDO..."
                                                    : exibirCampoColab(item.campo, linha.colab?.[item.campo])}
                                            </CelulaClicavel>
                                        ))}
                                        <CelulaClicavel
                                            vazia={!linha.deMunicip}
                                            onClick={() => abrirCelula(linha.id, "de", "celulaMunicipioPassagem")}
                                        >
                                            {formatarLocal(linha.deMunicip, linha.deEstado) || "SELECIONAR"}
                                        </CelulaClicavel>
                                        <CelulaClicavel
                                            vazia={!linha.paraMunicip}
                                            onClick={() => abrirCelula(linha.id, "para", "celulaMunicipioPassagem")}
                                        >
                                            {formatarLocal(linha.paraMunicip, linha.paraEstado) || "SELECIONAR"}
                                        </CelulaClicavel>
                                        <CelulaClicavel
                                            vazia={!linha.horario}
                                            onClick={() => abrirCelula(linha.id, "horario", "celulaHorarioPassagem")}
                                        >
                                            {formatarHorario(linha.horario) || "SELECIONAR"}
                                        </CelulaClicavel>
                                        <CelulaAcao>
                                            {linhas.length > 1 && (
                                                <Lixeira onClick={() => removerLinha(linha.id)} />
                                            )}
                                        </CelulaAcao>
                                    </tr>
                                ))}
                            </tbody>
                        </Tabela>
                    </TabelaWrapper>
                    <Rodape>
                        <BotaoAdicionarLinha onClick={adicionarLinha}>
                            <GoPlus />
                            ADICIONAR COLABORADOR
                        </BotaoAdicionarLinha>
                        <BotaoSalvar onClick={handleSalvar} disabled={salvando}>
                            {salvando ? "SALVANDO..." : "SALVAR"}
                        </BotaoSalvar>
                    </Rodape>
                </Modal>
            </Overlay>

            {isModalOpen("celulaInfoPassagem") && celula && (
                <ModalCelulaInfo
                    titulo={labelCelulaInfo}
                    valor={exibirCampoColab(celula.campo, linhaSelecionada?.colab?.[celula.campo])}
                    onClose={fecharCelula}
                />
            )}

            {isModalOpen("celulaMunicipioPassagem") && celula && (
                <ModalCelulaMunicipio
                    titulo={celula.campo === "de" ? "DE" : "PARA"}
                    valor={formatarLocal(
                        linhaSelecionada?.[`${celula.campo}Municip`],
                        linhaSelecionada?.[`${celula.campo}Estado`]
                    )}
                    onSave={salvarCelula}
                    onClose={fecharCelula}
                />
            )}

            {isModalOpen("celulaHorarioPassagem") && celula && (
                <ModalCelulaHorario
                    valor={linhaSelecionada?.horario}
                    onSave={salvarCelula}
                    onClose={fecharCelula}
                />
            )}
        </>
    )

}

export default ModalNovaSolicitacao;
